'use client';

import { createCartExperimentResolver } from './resolver';
import { CART_EXPERIMENT_CONFIG } from './config';
import { env } from '@/utils/env';
import logger from '@/utils/client-logger';
import type { CartExperimentConfig, CartExperimentStore } from './resolver';
import type { CartExperimentState } from './types';

export type { CartExperimentConfig, CartExperimentStore } from './resolver';

const resolver = createCartExperimentResolver(async (paramName, signal) => {
  const { getAbTestingData } = await import('@/api/public');
  // 模块加载完成前已因 reset/关闭/配置切换失效时，不再启动请求；已发出的 API 暂不支持物理取消。
  if (signal.aborted) throw new Error('Cart experiment resolution cancelled');
  const response = await getAbTestingData({ param_name: paramName });

  if (isCartExperimentDevelopment()) {
    logger.log('[Cart AB 接口返回]', {
      paramName,
      code: response?.code,
      group: response?.data?.strategy_variable,
    });
  }

  return response;
});

/** 联调资格仅由业务环境和站点决定，不依赖 NODE_ENV 构建模式。 */
export function isCartExperimentDevelopment(): boolean {
  return (
    (env.NEXT_PUBLIC_ENV === 'local' ||
      env.NEXT_PUBLIC_ENV === 'dev' ||
      env.NEXT_PUBLIC_ENV === 'test') &&
    env.NEXT_PUBLIC_SITE_NAME === 'us'
  );
}

/** US local/dev/test 且显式开启开关时接入真实分桶；uat/production 业务环境仍关闭。 */
export function getCartExperimentConfig(): CartExperimentConfig {
  return isCartExperimentDevelopment() && env.NEXT_PUBLIC_ENABLE_CART_VERSION_EXPERIMENT === 'true'
    ? CART_EXPERIMENT_CONFIG
    : { enabled: false };
}

/** 由客户端生命周期/事件调用，禁止 render 中调用；Store 必须来自当前 Provider。 */
export function resolveCartExperiment(
  store: CartExperimentStore,
  config: CartExperimentConfig = { enabled: false },
): Promise<CartExperimentState> {
  return resolver.resolve(store, config);
}

/** Header 与页面共用真实实验结果；URL 参数不能覆盖服务端分桶或关闭实验。 */
export function resolveBrowserCartExperiment(
  store: CartExperimentStore,
): Promise<CartExperimentState> {
  if (typeof window === 'undefined') return Promise.resolve({ status: 'disabled' });
  return resolver.resolve(store, getCartExperimentConfig());
}

/** 仅供安全的重新初始化；登录/领券、单个消费者卸载均不应调用。 */
export function resetCartExperiment(store: CartExperimentStore): void {
  resolver.reset(store);
}
